import { useDispatch } from "react-redux";
import clsx from "clsx";

import Button from "/components/common/button";
import Loader from "/components/common/loader";
import { showPopup } from "../../../../redux/action-creaters/popup";
import { popupTypes } from "../../../../redux/reducers/popupReducer";

import { usePopupForm } from "./usePopupForm";
import FormSuccess from "./FormSuccess";

import styles from "./index.module.scss";

/**
 * Универсальная обёртка для попап-форм (UI + стили). Логика формы — в `usePopupForm`.
 *
 * Каждая конкретная форма — это по сути набор декларативных props:
 *   - `code` — код формы в Bitrix;
 *   - подпись кнопки отправки приходит с бэка в `form.button`;
 *   - `filterField(field)` — фильтр полей;
 *   - `goalOpen/goalSent` — цели метрики (вызывается reachGoal);
 *   - инпуты, оферта и кнопка отправки — из хука (`inputFieldElements`, `checkboxOfferInner`, `submitButtonElement`), контейнеры стилизует обёртка;
 *   - `extraData({ fieldValues, popupData, form })` — доп. поля к submit;
 *   - `successText` — строка или `(response) => string`: переопределяет текст отбивки (иначе — универсальная отбивка по времени МСК из панели);
 *   - `onSuccess({ response, fieldValues, popupData })` — сайд-эффекты после успешной отправки;
 *   - `onAfterSubmit(...)` — после любой отправки;
 *   - `children` — произвольный контент над полями (например, <Mini/>).
 */
export default function PopupFormWrapper({
  code,
  goalOpen,
  goalSent,
  test = "",
  className = "",
  children,
  filterField,
  extraData,
  successText,
  onSuccess,
  onAfterSubmit,
  hideFormDescription = false,
  showSuccessCloseButton = false,
  formId = "popupForm",
}) {
  const dispatch = useDispatch();

  const {
    form,
    isLoading,
    result,
    agreeError,
    inputFieldElements,
    checkboxOfferInner,
    submitButtonElement,
  } = usePopupForm({
    code,
    goalOpen,
    goalSent,
    filterField,
    extraData,
    successText,
    onSuccess,
    onAfterSubmit,
    formId,
  });

  const onHide = () => {
    dispatch(showPopup(popupTypes.none));
  };

  return (
    <>
      {!result.isSuccess && (
        <h2 data-test={test} className={styles.Title}>
          {form.title}
        </h2>
      )}
      {isLoading && (
        <div className={styles.Loader}>
          <Loader />
        </div>
      )}
      {result.isSuccess && (
        <>
          <FormSuccess reply={result.reply} />
          {showSuccessCloseButton ? (
            <div className={styles.Buttom}>
              <Button onClick={onHide} label={"Закрыть"} />
            </div>
          ) : null}
        </>
      )}
      {!isLoading && !result.isSuccess && (
        <form className={`${styles.Content} ${className}`} id={formId}>
          {!hideFormDescription && form.description && (
            <div className={styles.TopText}>{form.description}</div>
          )}
          {result.message && (
            <span className={styles.Error}>{result.message}</span>
          )}
          {children}
          {inputFieldElements}
          {checkboxOfferInner != null && (
            <div
              className={clsx(styles.Oferta, agreeError && styles.OfertaError)}
            >
              {checkboxOfferInner}
            </div>
          )}
          <div className={styles.Buttom}>{submitButtonElement}</div>
        </form>
      )}
    </>
  );
}
